import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap, type Schemas } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, Card, Notice, TextField } from './ui.js';

/** The fields of a site password; shared by "add site" and the site settings (T-49). */
export function BasicAuthFields({
  username,
  password,
  onUsername,
  onPassword,
  passwordHint,
}: {
  username: string;
  password: string;
  onUsername: (v: string) => void;
  onPassword: (v: string) => void;
  passwordHint?: string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <TextField
        label={t('basicAuth.username')}
        autoComplete="off"
        maxLength={200}
        value={username}
        onChange={(e) => onUsername(e.target.value)}
      />
      <TextField
        label={t('basicAuth.password')}
        hint={passwordHint}
        type="password"
        autoComplete="new-password"
        maxLength={500}
        value={password}
        onChange={(e) => onPassword(e.target.value)}
      />
    </div>
  );
}

/** Site settings: store, change or remove the password of a protected site. */
export function BasicAuthForm({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [username, setUsername] = useState(site.basicAuth?.username ?? '');
  const [password, setPassword] = useState('');
  const path = { params: { path: { siteId: site.id } } };
  const done = () => {
    setPassword('');
    void client.invalidateQueries({ queryKey: ['site', site.id] });
  };
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT('/sites/{siteId}/basic-auth', {
          ...path,
          body: { username: username.trim(), password },
        }),
      ),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE('/sites/{siteId}/basic-auth', path)),
    onSuccess: () => {
      setUsername('');
      done();
    },
  });
  const error = save.error ?? remove.error;
  const stored = Boolean(site.basicAuth);
  // Linked from the "password protected" notice on the dashboard.
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (window.location.hash === '#basic-auth') heading.current?.scrollIntoView();
  }, []);
  return (
    <Card>
      <h2 ref={heading} id="basic-auth" className="mb-1 text-xl font-semibold">
        {t('basicAuth.title')}
      </h2>
      <p className="mb-2 text-slate-800">{t('basicAuth.text')}</p>
      <ul className="mb-3 list-disc pl-5 text-sm text-slate-700">
        <li>{t('basicAuth.safe1')}</li>
        <li>{t('basicAuth.safe2')}</li>
        <li>{t('basicAuth.safe3')}</li>
      </ul>
      {stored ? (
        <Notice kind="success">
          {t('basicAuth.stored', { username: site.basicAuth?.username })}
        </Notice>
      ) : null}
      <form
        className="mt-3 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (username.trim() && password) save.mutate();
        }}
      >
        <BasicAuthFields
          username={username}
          password={password}
          onUsername={setUsername}
          onPassword={setPassword}
          passwordHint={stored ? t('basicAuth.newPasswordHint') : undefined}
        />
        {error ? <Notice kind="error">{errorMessage(error, t)}</Notice> : null}
        {save.isSuccess ? <Notice kind="success">{t('basicAuth.saved')}</Notice> : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" busy={save.isPending} disabled={!username.trim() || !password}>
            {t('basicAuth.save')}
          </Button>
          {stored ? (
            <Button variant="secondary" busy={remove.isPending} onClick={() => remove.mutate()}>
              {t('basicAuth.remove')}
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
